import React from "react";
import { useErrorLogger } from "./ErrorLoggerProvider";
import "./ErrorFallback.css";

export interface ErrorFallbackProps {
  error: any;
  reset: () => void;
  boundaryLabel?: string;
}

export const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  reset,
  boundaryLabel,
}) => {
  const { logs } = useErrorLogger();
  const [showDetails, setShowDetails] = React.useState(false);

  const copyDetails = () => {
    const payload = {
      message: error?.message || String(error),
      stack: error?.stack,
      boundary: boundaryLabel,
      recentLogs: logs.slice(-10),
      userAgent:
        typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      time: new Date().toISOString(),
    };
    navigator.clipboard
      .writeText(JSON.stringify(payload, null, 2))
      .catch(() => {});
  };

  return (
    <div className="error-fallback-container">
      <div className="error-fallback-paper">
        <div className="error-fallback-content">
          <div className="error-fallback-header">
            <span className="error-icon">⚠️</span>
            <h2>Something went wrong</h2>
          </div>
          {boundaryLabel && (
            <p className="error-boundary-label">
              Boundary: {boundaryLabel}
            </p>
          )}
          <p>
            An unexpected error occurred. You can try refreshing the page or
            going back. If the problem continues please provide the error
            details to support.
          </p>
          <div className="error-fallback-buttons">
            <button
              className="error-btn error-btn-primary"
              onClick={() => window.location.reload()}
            >
              🔄 
            </button>
            <button
              className="error-btn error-btn-outline"
              onClick={() =>
                window.history.length > 1
                  ? window.history.back()
                  : window.location.assign("/")
              }
            >
              ← Back
            </button>
            <button
              className={`error-btn ${showDetails ? 'error-btn-outline' : 'error-btn-text'}`}
              onClick={() => setShowDetails((s) => !s)}
            >
              {showDetails ? '✕ Hide details' : '🐛 Show details'}
            </button>
            {showDetails && (
              <button
                className="error-btn error-btn-text"
                onClick={copyDetails}
              >
                📋 Copy
              </button>
            )}
            <button className="error-btn error-btn-text" onClick={reset}>
              Dismiss
            </button>
          </div>
          {showDetails && (
            <div className="error-details">
              <div className="error-details-content">
                <h4>{error?.message || String(error)}</h4>
                <pre className="error-stack">
                  {error?.stack}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorFallback;
